import angular, { auto, IRootScopeService } from 'angular'
import { useContext, useEffect, useState } from 'react'

import { PERMISSIONS, RULES } from '../constants'
import { quickKeysManager } from '../managers/quick-keys-manager'
import { wrContext } from './wrapper'

let $rootScope: IRootScopeService

angular.module('webregister').run([
  '$injector',
  (i: auto.IInjectorService) => {
    $rootScope = i.get('$rootScope')
  },
])

export const focusGlobalSearch = () => {
  $rootScope.$emit('autocomplete:global-search:focus', {
    isFocusable: true,
    focusDelay: 200,
  })
}

// vend.js
export const useVdTracking = () => useContext(wrContext).vdTracking
export const useVdFeatureManager = () => useContext(wrContext).vdFeatureManager

// vend.ui
export const useVdCurrencyFilter = () => useContext(wrContext).vdCurrencyFilter

// webregister
export const useRegisterManager = () => useContext(wrContext).registerManager

export const useSaleInventoryData = () =>
  useContext(wrContext).saleInventoryData
export const useSaleManager = () => useContext(wrContext).saleManager
export const useCustomerManager = () => useContext(wrContext).customerManager
export const useTaxManager = () => useContext(wrContext).taxManager
export const useRetailerSettingsManager = () =>
  useContext(wrContext).retailerSettingsManager
export const useSaleTracker = () => useContext(wrContext).saleTracker
export const useUserManager = () => useContext(wrContext).userManager
export const useSaleLoader = () => useContext(wrContext).saleLoader
export const usePaymentManager = () => useContext(wrContext).paymentManager
export const useCashMovementsManager = () =>
  useContext(wrContext).cashMovementsManager
export const useRegisterClosureManager = () =>
  useContext(wrContext).registerClosureManager
export const useSaleSyncManager = () => useContext(wrContext).saleSyncManager
export const useWebRegisterStatusManager = () =>
  useContext(wrContext).webRegisterStatusManager
export const useUserSwitchingService = () =>
  useContext(wrContext).userSwitchingService
export const useProductEmbeddedBarcodeService = () =>
  useContext(wrContext).productEmbeddedBarcodeService
export const usePosStateManager = () => useContext(wrContext).posStateManager
export const useCustomersStore = () => useContext(wrContext).customersStore
export const useUsersStore = () => useContext(wrContext).usersStore
export const useProductsStore = () => useContext(wrContext).productsStore
export const useSaleTransformer = () => useContext(wrContext).saleTransformer
export const useHotkeysService = () => useContext(wrContext).hotkeysService
export const useTrainingModeManager = () =>
  useContext(wrContext).trainingModeManager
export const useProductSuggestions = () =>
  useContext(wrContext).productSuggestions
export const useQuickKeysManager = () => quickKeysManager
export const useQkNodesResource = () => useContext(wrContext).qkNodesResource
export const useSyncManager = () => useContext(wrContext).syncManager
export const useLoyaltyManager = () => useContext(wrContext).loyaltyManager
export const useReceiptSettingsStore = () =>
  useContext(wrContext).receiptSettingsStore
export const useWebRegisterManager = () =>
  useContext(wrContext).webRegisterManager
/**
 * Hook that returns the value of a permission for the current user.
 * Causes a re-render on user switch when permission changes.
 *
 * @param permission The permission to check.
 *
 * @returns true if the user has the permission.
 */
export const useUserPermission = (permission: PERMISSIONS) => {
  const userManager = useUserManager()

  const [hasPermission, setHasPermission] = useState<boolean>(() =>
    userManager.hasPermission(permission)
  )

  useEffect(() => {
    const updatePermission = () =>
      setHasPermission(userManager.hasPermission(permission))

    userManager.on('loggedInUserChange', updatePermission)

    return () => userManager.off('loggedInUserChange', updatePermission)
  }, [permission, userManager])

  return hasPermission
}

export const useWorkflowsManager = () => useContext(wrContext).workflowsManager

/**
 * Hook that returns the value of a rule for the current user.
 * Causes a re-render on user switch when rule changes.
 *
 * @param ruleName The rule to check.
 *
 * @returns {Rule | null} if the current user has the rule
 */
export const useUserRule = (ruleName: RULES) => {
  const userManager = useUserManager()

  const [rule, setRule] = useState<any | null>(() =>
    userManager.getRule(ruleName)
  )

  useEffect(() => {
    const updateRule = () => setRule(userManager.getRule(ruleName))
    userManager.on('loggedInUserChange', updateRule)

    return () => userManager.off('loggedInUserChange', updateRule)
  }, [ruleName, userManager])

  return rule
}

export const useUserHasLineItemDiscountRule = () => {
  const userManager = useUserManager()

  const [hasDiscountRule, setHasDiscountRule] = useState<boolean>(() =>
    userManager.canApplyLineItemDiscount()
  )

  useEffect(() => {
    const updateDiscountRule = () =>
      setHasDiscountRule(userManager.canApplyLineItemDiscount())
    userManager.on('loggedInUserChange', updateDiscountRule)

    return () => userManager.off('loggedInUserChange', updateDiscountRule)
  }, [userManager])

  return hasDiscountRule
}

export const useSaleCalculationComplete = () => {
  const [hasCalculated, setHasCalculated] = useState<boolean>(false)
  const saleManager = useSaleManager()
  const updateCalculated = () => {
    setHasCalculated(true)
  }
  useEffect(() => {
    saleManager.on('saleManager:saleCalculating:end', updateCalculated)
    return () => {
      saleManager.off('saleManager:saleCalculating:end', updateCalculated)
    }
  }, [saleManager])

  return hasCalculated
}

export const useIsCalculatingSale = () => {
  const saleManager = useSaleManager()
  const [calculating, setCalculating] = useState<boolean>(
    saleManager.saleCalculating
  )

  useEffect(() => {
    const setStart = () => setCalculating(true)
    const setEnd = () => setCalculating(false)
    saleManager.on('saleManager:saleCalculating:start', setStart)
    saleManager.on('saleManager:saleCalculating:end', setEnd)
    return () => {
      saleManager.off('saleManager:saleCalculating:start', setStart)
      saleManager.off('saleManager:saleCalculating:end', setEnd)
    }
  }, [saleManager])

  return calculating
}
export const useVdEscapeStack = () => useContext(wrContext).vdEscapeStack
